import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Plus, Trash2 } from "lucide-react";

const TRIGGER_FIELDS = [
  { value: "industry_primary", label: "Отрасль" },
  { value: "stage", label: "Стадия" },
  { value: "business_model", label: "Бизнес-модель" },
  { value: "revenue_bucket", label: "Выручка" },
];

const DEFAULT_WEIGHTS = {
  investor: { stage_product: 15, revenue_growth: 25, customers_sales: 15, market_scalability: 15, team: 10, investor_readiness: 10, ip_barriers: 5, risk_reduction: 5 },
  corporate: { business_value: 20, pilots_adoption: 25, measurable_effect: 15, integration_readiness: 10, b2b_b2g_fit: 10, industry_fit: 10, team_delivery: 5, implementation_risk: 5 },
  institutional: { tech_novelty: 20, priority_alignment: 15, rnd_ip_science: 15, grant_readiness: 15, impact: 10, feasibility_stage: 10, team_partners: 10, documentation: 5 },
};

export default function ScoringProfiles() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [editing, setEditing] = useState(null);   // editing profile object
  const [error, setError] = useState("");

  const load = () => api.get("/api/admin/scoring-profiles").then((r) => setProfiles(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    name: "Новая шкала",
    description: "",
    is_default: false,
    is_active: true,
    priority: 10,
    triggers: [],
    weights: JSON.parse(JSON.stringify(DEFAULT_WEIGHTS)),
  });

  const save = async () => {
    setError("");
    try {
      if (editing.id) {
        await api.put(`/api/admin/scoring-profiles/${editing.id}`, stripIds(editing));
      } else {
        await api.post("/api/admin/scoring-profiles", stripIds(editing));
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Удалить шкалу «${p.name}»?`)) return;
    try { await api.delete(`/api/admin/scoring-profiles/${p.id}`); await load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const isSuper = user?.role === "superadmin";

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mono">ШКАЛЫ ВЕСОВ</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-2">{profiles.length} <span className="text-foreground/40">профилей</span></h1>
        </div>
        {isSuper && (
          <button className="btn-primary" onClick={startNew} data-testid="profile-new-btn"><Plus size={14} /> Новая шкала</button>
        )}
      </div>

      <div className="border border-black/10">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-black/10">
            <tr className="label-mono">
              <th className="text-left px-4 py-3">Название</th>
              <th className="text-left px-4 py-3">Версия</th>
              <th className="text-left px-4 py-3">Триггеры</th>
              <th className="text-right px-4 py-3">Приоритет</th>
              <th className="text-left px-4 py-3">Default</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => (
              <tr key={p.id} className="border-b border-black/5">
                <td className="px-4 py-3" data-testid={`profile-row-${p.id}`}>{p.name}</td>
                <td className="px-4 py-3 mono text-xs">{p.version}</td>
                <td className="px-4 py-3 text-xs text-foreground/70">{(p.triggers || []).length} условий</td>
                <td className="px-4 py-3 mono text-right">{p.priority}</td>
                <td className="px-4 py-3 text-xs">{p.is_default ? "✓" : ""}</td>
                <td className="px-4 py-3 text-xs">{p.is_active ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-right space-x-3">
                  {isSuper && <button className="hop-link text-xs" onClick={() => setEditing(JSON.parse(JSON.stringify(p)))} data-testid={`profile-edit-${p.id}`}>Редактировать</button>}
                  {isSuper && !p.is_default && (
                    <button className="hop-link text-xs text-destructive" onClick={() => remove(p)} data-testid={`profile-delete-${p.id}`}><Trash2 size={12} className="inline" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProfileEditor
          editing={editing}
          setEditing={setEditing}
          onCancel={() => setEditing(null)}
          onSave={save}
          error={error}
        />
      )}
    </div>
  );
}

function stripIds(p) {
  // remove server-managed fields before sending
  const out = { ...p };
  ["id", "version", "created_at", "updated_at"].forEach((k) => delete out[k]);
  return out;
}

function ProfileEditor({ editing, setEditing, onCancel, onSave, error }) {
  const update = (patch) => setEditing({ ...editing, ...patch });
  const setWeight = (idx, comp, val) => {
    const w = { ...editing.weights, [idx]: { ...editing.weights[idx], [comp]: Number(val) } };
    update({ weights: w });
  };
  const addTrigger = () => update({ triggers: [...(editing.triggers || []), { field: "industry_primary", operator: "equals", value: "" }] });
  const updateTrigger = (i, patch) => {
    const arr = [...(editing.triggers || [])];
    arr[i] = { ...arr[i], ...patch };
    update({ triggers: arr });
  };
  const removeTrigger = (i) => update({ triggers: (editing.triggers || []).filter((_, idx) => idx !== i) });

  const sumWeight = (idx) => Object.values(editing.weights[idx] || {}).reduce((a, b) => a + Number(b || 0), 0);

  return (
    <div className="grid-cell space-y-6" data-testid="profile-editor">
      <div className="flex items-center justify-between">
        <div className="label-mono">{editing.id ? "РЕДАКТИРОВАНИЕ" : "НОВАЯ ШКАЛА"}</div>
        <div className="flex gap-3">
          <button className="btn-secondary" onClick={onCancel}>Отмена</button>
          <button className="btn-primary" onClick={onSave} data-testid="profile-save-btn">Сохранить</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <div className="label-mono mb-1">Название</div>
          <input className="field-input" value={editing.name} onChange={(e) => update({ name: e.target.value })} data-testid="profile-name-input" />
        </label>
        <label className="block">
          <div className="label-mono mb-1">Приоритет (выше = важнее)</div>
          <input type="number" className="field-input" value={editing.priority} onChange={(e) => update({ priority: Number(e.target.value) })} />
        </label>
        <label className="block md:col-span-2">
          <div className="label-mono mb-1">Описание</div>
          <input className="field-input" value={editing.description || ""} onChange={(e) => update({ description: e.target.value })} />
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={editing.is_active} onChange={(e) => update({ is_active: e.target.checked })} />
          <span className="text-sm">Активна</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={editing.is_default} onChange={(e) => update({ is_default: e.target.checked })} />
          <span className="text-sm">Дефолтная (применяется если ни один триггер не сработал)</span>
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="label-mono">ТРИГГЕРЫ</div>
          <button className="btn-ghost" onClick={addTrigger}><Plus size={12} /> добавить</button>
        </div>
        {(editing.triggers || []).length === 0 && (
          <p className="text-xs text-foreground/60">Без триггеров профиль может быть только дефолтным.</p>
        )}
        <div className="space-y-2">
          {(editing.triggers || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <select className="field-input" value={t.field} onChange={(e) => updateTrigger(i, { field: e.target.value })}>
                {TRIGGER_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <select className="field-input max-w-[120px]" value={t.operator || "equals"} onChange={(e) => updateTrigger(i, { operator: e.target.value })}>
                <option value="equals">равно</option>
                <option value="in">в списке</option>
              </select>
              <input className="field-input" placeholder={t.operator === "in" ? "v1,v2,v3" : "значение"} value={Array.isArray(t.value) ? t.value.join(",") : t.value || ""}
                onChange={(e) => updateTrigger(i, { value: t.operator === "in" ? e.target.value.split(",").map((s) => s.trim()).filter(Boolean) : e.target.value })} />
              <button className="btn-ghost" onClick={() => removeTrigger(i)}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="label-mono mb-3">ВЕСА КОМПОНЕНТОВ (Σ должна быть около 100)</div>
        <div className="grid md:grid-cols-3 gap-4">
          {["investor", "corporate", "institutional"].map((idx) => (
            <div key={idx} className="grid-cell">
              <div className="flex items-baseline justify-between">
                <div className="label-mono">{idx.toUpperCase()}</div>
                <div className="mono text-xs text-foreground/60">Σ {sumWeight(idx)}</div>
              </div>
              <div className="mt-3 space-y-2">
                {Object.entries(editing.weights[idx] || {}).map(([k, v]) => (
                  <label key={k} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 truncate">{k}</span>
                    <input type="number" className="field-input mono w-16 text-right" value={v} onChange={(e) => setWeight(idx, k, e.target.value)} />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && <p className="text-destructive label-mono">{error}</p>}
    </div>
  );
}
